/**
 * Embedded authentication provider
 * Delegates authentication to parent application
 */

import type { IAuthProvider } from './types'
import type { User } from '../types/chat'

export interface EmbeddedAuthConfig {
  getCurrentUser: () => User | null
  getToken: () => string | null
  onAuthChange?: (callback: (user: User | null) => void) => () => void
}

export class EmbeddedAuthProvider implements IAuthProvider {
  private config: EmbeddedAuthConfig

  constructor(config: EmbeddedAuthConfig) {
    this.config = config
  }

  getCurrentUser(): User | null {
    return this.config.getCurrentUser()
  }

  getToken(): string | null {
    return this.config.getToken()
  }

  async login(_username: string, _password: string): Promise<{ user: User; token: string }> {
    // In embedded mode, login is handled by parent app
    throw new Error('Login should be handled by parent application in embedded mode')
  }

  logout(): void {
    // In embedded mode, logout is handled by parent app
    throw new Error('Logout should be handled by parent application in embedded mode')
  }

  isAuthenticated(): boolean {
    return !!this.getToken()
  }

  onAuthChange(callback: (user: User | null) => void): () => void {
    if (this.config.onAuthChange) {
      return this.config.onAuthChange(callback)
    }

    // No-op if parent doesn't provide auth change listener
    return () => {
      // Empty unsubscribe
    }
  }
}
